import { JsonServiceClient } from "@servicestack/client";

/**
 * Creates the application's JSON client and installs its browser error redirects.
 *
 * Postconditions: Returns a new client with the supplied base URL, a nullish base-path fallback
 * and an empty custom-header collection. Construction does not send an application request.
 *
 * Assumptions: Headers is available at construction. Error handling runs in a browser and expects
 * error.responseStatus.message whenever the status is not 401; malformed errors can throw.
 */
export const DefaultClient = (baseUrl?: string, basePath?: string, theme?: string) => {
    const client = new JsonServiceClient(baseUrl).apply(c => {
        c.basePath = basePath ?? null;
        // Replace the client's custom headers; request construction remains with ServiceStack.
        c.headers = new Headers();
    });

    /**
     * Requests sign-in or an error page for the existing status/message conditions.
     *
     * Postconditions: A matching error assigns window.location.href; this requests navigation
     * without guaranteeing completion. Other errors leave the current location unchanged.
     *
     * Assumptions: Unauthorized status/message takes precedence over 403/404. Sign-in preserves
     * pathname and search, omits the fragment and interpolates the theme without encoding it.
     * This callback neither retries the request nor establishes successful authentication.
     */
    client.exceptionFilter = (res: Response, error: any) => {
        if (res.status === 401 || error.responseStatus.message === "Unauthorized") {
            window.location.href = `/auth/openiddict?redirect=${encodeURIComponent(location.pathname + location.search)}&theme=${theme}`;
        } else if (res.status == 403 || res.status == 404) {
            window.location.href = "/error/" + res.status;
        }
    };

    return client;
};

export default DefaultClient;
